import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import api from '../services/api';
import { isPaidSeatEnforced } from '@/lib/trialSeats';

/** Shape of the 402 SEAT_LIMIT_REACHED payload (contract §5). */
export interface SeatLimitError {
  error: string;
  code: 'SEAT_LIMIT_REACHED';
  used_seats: number;
  purchased_slots: number;
}

/**
 * Detect the hard-block 402 returned by seat-assign when the paid capacity is
 * exceeded (contract §5). Callers use this to drive the MVP UX: redirect to
 * `/billing` with a "buy more seats" toast/modal. Inline quick-buy is a
 * documented fast-follow, not required for MVP.
 */
export function isSeatLimitError(err: unknown): err is AxiosError<SeatLimitError> {
  const axiosErr = err as AxiosError<Partial<SeatLimitError>>;
  return (
    axiosErr?.response?.status === 402 &&
    axiosErr.response?.data?.code === 'SEAT_LIMIT_REACHED'
  );
}

/**
 * À la carte per-module billing hooks.
 *
 * Plan-based billing (`usePlans`, 3-package grid) was removed on the backend in
 * the à la carte redesign (P1–P3). Pricing now lives per-module on `ModulePrice`
 * and the org is billed by purchased seat capacity (`purchased_slots`):
 *
 *   monthly_total = Σ_modules ( base_price + max(0, purchased_slots − included_seats) × seat_price )
 *
 * See docs/HANDOFF-alacarte-frontend.md for the full API contract.
 */

/**
 * Zakaj sedež ta hip (ne) omejuje — `seat_capacity[modul].reason` (T1420).
 *
 * `subscribed` nosi plačano kapaciteto. T3010: `trial`/`promo` z
 * `enforced === true` nosita **mejo oseb v preizkusu** (`trial_max_seats`), ne
 * plačanih sedežev — za plačane sedeže beri `isPaidSeatEnforced`, za mejo
 * `trialSeatLimit`. `promo_subscribed` (T2996 c) ima `enforced === false`, a
 * številke kupljenih sedežev, ki jih porabijo zunanji.
 */
export type SeatCapacityReason =
  /** Naročeno in uveljavljeno — `total` je plačana kapaciteta. */
  | 'subscribed'
  /**
   * Preizkusno obdobje — plačani sedeži ne omejujejo; T3010: z `enforced`
   * velja meja oseb v preizkusu (`total` = meja, `used` = zasedena mesta).
   */
  | 'trial'
  /** Veljavna promocija brez naročnine — isto kot preizkus (`_is_free_period`). */
  | 'promo'
  /**
   * T2996 (c): naročena organizacija z veljavno promocijo. Članov sedež ne
   * omejuje, zunanji pa porabijo kupljen sedež — `total`/`free`/`used` so
   * številke `purchased_slots`, `enforced` pa `false`.
   */
  | 'promo_subscribed'
  /** Modul je deklarirano brezplačen — `has_module_seat` vrne vedno `true`. */
  | 'free_module'
  /** Ni naročnine in ni brezplačnega obdobja — sedež ne podeli ničesar. */
  | 'no_access'
  /**
   * T2435: plačljiv modul je naročen, a sedeža zanj nihče ne preverja
   * (zunaj `SEAT_ENFORCED_MODULES`) — dostop imajo vsi člani. Pride samo iz
   * matrike (`billing/<org>/seats/`), nikoli iz `subscription/`.
   */
  | 'not_enforced';

/**
 * Koliko sedežev enega modula je prostih — **brez tega, kdo jih drži** (T1420).
 *
 * Odgovor je namenoma ozek: samo skalarji, nobenega seznama in nobenega
 * e-naslova. Kdo drži kateri sedež, ostane v matriki (`useModuleSeats`,
 * `IsOrganizationAdmin`); ta števec je dosegljiv vsakemu **članu**, ker ga
 * potrebuje že vodja projekta, ki sme podeliti dostop zunanjemu sodelavcu.
 *
 * `enforced === false` ni kozmetika: med preizkusom je plačana kapaciteta
 * navadno 0, zato bi vmesnik brez te ločnice izrisal »0 od 0« — trditev, da ni
 * prostih sedežev, medtem ko jih v resnici nič ne omejuje.
 */
export interface SeatCapacity {
  enforced: boolean;
  reason: SeatCapacityReason;
  /** Plačana kapaciteta (ali meja v preizkusu, T3010); `null`, kadar ni številke. */
  total: number | null;
  /** Prosti sedeži/mesta; `null`, kadar ni številke. */
  free: number | null;
  /** T3010: zasedeni sedeži/mesta; `null` brez številke. Starejši odgovori polja nimajo. */
  used?: number | null;
  /**
   * T3096: samo v preizkusu z mejo — koliko od `used` so čakajoča povabila
   * novih oseb (vrata povabila jih štejejo, zato jih šteje tudi števec).
   */
  pending?: number;
}

// T3010: pomočniki za mejo oseb v preizkusu živijo v `@/lib/trialSeats` (čiste
// funkcije — testi, ki ta modul nadomestijo z mockom, jih ne izgubijo).
export {
  isPaidSeatEnforced,
  isTrialSeatLimitError,
  trialSeatLimit,
  type TrialSeatLimit,
  type TrialSeatLimitError,
} from '@/lib/trialSeats';

export interface Subscription {
  id: number;
  status: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'expired' | 'disputed';
  billing_cycle: 'monthly' | 'annual';
  trial_ends_at: string | null;
  current_period_end: string | null;
  custom_price_override: string | null;
  is_active: boolean;
  trial_active: boolean;
  subscribed_modules: string[];
  seats_by_module: Record<string, number>;
  /**
   * T1420: `{modul: {enforced, reason, total, free}}` — prosti sedeži za
   * **člana**. Ključi so samo moduli iz `SEAT_ENFORCED_MODULES`, torej tisti,
   * pri katerih sedež sploh kaj pomeni; modula, ki ga tu ni, vmesnik ne sme
   * komentirati (»brez podatka ni trditve«).
   */
  seat_capacity: Record<string, SeatCapacity>;
  monthly_total: string;
  /**
   * T2964: znesek naročnine za **njen** cikel (letna → letni znesek). Kartica
   * ga kaže z »/ leto« ali »/ mesec«; `monthly_total` je pri letni naročnini
   * le dvanajstina. Starejši odgovori polja nimajo.
   */
  cycle_total?: string;
  /**
   * T2969: cikel prvega plačila, ki čaka na potrditev (Mollie); `null` = nič
   * ne čaka. Med čakanjem je `cycle_total` znesek tega cikla, `billing_cycle`
   * pa še dosedanji — preklopi se šele ob plačilu. Starejši odgovori polja
   * nimajo.
   */
  pending_billing_cycle?: 'monthly' | 'annual' | null;
  /**
   * T2971: načrtovan preklop **aktivne** mesečne naročnine na letno; `null` =
   * ni ga. Letna začne veljati ob `scheduled_cycle_starts_at` (konec tekočega
   * obdobja); do takrat sta `billing_cycle` in `cycle_total` še mesečna.
   */
  scheduled_billing_cycle?: 'monthly' | 'annual' | null;
  scheduled_cycle_starts_at?: string | null;
  limits: {
    max_projects: number | null;
    /** T3010: meja oseb v preizkusu (`null` = brez meje); starejši odgovori je nimajo. */
    trial_max_seats?: number | null;
  };
  /** T2029: paketi dodatnega prostora (starejši odgovori polja nimajo). */
  storage_addon?: StorageAddonState;
}

/** A single ModulePrice row (Sysadmin Module Pricing, contract §7/§8). */
export interface ModulePrice {
  id: number;
  module: number;
  module_slug: string;
  module_name: string;
  sort_order: number;
  is_addon: boolean;
  billing_cycle: 'monthly' | 'annual';
  base_price: string;
  included_seats: number;
  seat_price: string;
  currency: string;
  is_active: boolean;
}

/**
 * T2029 — stanje dodatnega prostora na naročnini. `purchase_blocker` je koda
 * razloga, zakaj nakup ta hip ni mogoč (`null` = mogoč).
 */
export interface StorageAddonState {
  packs: number;
  cancelling: number;
  pending: number;
  billable: number;
  step_bytes: number;
  unit_price: string;
  monthly_unit_price: string;
  billing_cycle: 'monthly' | 'annual';
  max_per_purchase: number;
  purchase_blocker: 'subscription_required' | 'custom_price' | 'payment_pending' | null;
}

export interface StorageAddonResult {
  status: 'active' | 'pending' | 'reinstated' | 'cancelling';
  storage_addon: StorageAddonState;
}

/** A line item in the cost summary (contract §2). */
export interface SummaryLineItem {
  module_slug: string;
  module_name: string;
  description: string;
  base_price: string;
  seat_overage: number;
  seat_price: string;
  amount: string;
  /** T2029: samo vrstica `storage_addon` — število paketov in velikost paketa. */
  quantity?: number;
  step_bytes?: number;
}

/** Amount summary / live cost breakdown for subscribed modules (contract §2). */
export interface SubscriptionSummary {
  total: string;
  currency: string;
  billing_cycle: 'monthly' | 'annual';
  override_applied: boolean;
  line_items: SummaryLineItem[];
}

export interface Invoice {
  id: number;
  mollie_payment_id: string;
  amount: string;
  currency: string;
  status: string;
  created_at: string;
  updated_at: string;
  pdf_file: string | null;
  checkout_url: string;
  period_start: string | null;
  period_end: string | null;
  invoice_number: string;
  invoice_date: string | null;
  due_date: string | null;
  description: string;
  net_amount: string | null;
  vat_rate: string | null;
  vat_amount: string | null;
  vat_treatment: string;
  buyer_name: string;
  buyer_vat_id: string;
  buyer_company_reg: string;
  buyer_address: string;
  /** Per-module ' + '-joined module names (à la carte invoices). Optional for
   * older invoices that pre-date the redesign. */
  modules?: string;
  /** T1758: dobropisi, izdani k računu ob vračilu kupnine. */
  credit_notes?: CreditNote[];
  /** T1014 (S6): postavke računa (ime modula v jeziku uporabnika). */
  line_items?: InvoiceLineItem[];
}

/** T1014 (S6): postavka računa, kot jo vrne organizacijski seznam računov. */
export interface InvoiceLineItem {
  /** T3160: vrstica sorazmernega dela (opis nosi »sorazmerni del / prorated«). */
  is_prorated?: boolean;
  id: number;
  module_slug: string | null;
  module_name: string;
  description: string;
  quantity: string;
  vat_rate: string;
  net_amount: string;
  vat_amount: string;
  gross_amount: string;
}

/** T1758: dobropis (vračilo kupnine) — zneski so pozitivni, prikaz z minusom. */
export interface CreditNote {
  id: number;
  credit_note_number: string | null;
  credit_note_date: string | null;
  original_invoice_number: string;
  original_invoice_date: string | null;
  amount: string;
  net_amount: string;
  vat_amount: string;
  currency: string;
  reason: string;
  pdf_file: string | null;
  created_at: string;
}

export interface ModuleCheckoutRequest {
  module_slugs: string[];
  seats?: Record<string, number>;
  billing_cycle: 'monthly' | 'annual';
  redirect_url?: string;
  payment_method?: string;
}

export function useSubscription(orgSlug: string | undefined) {
  return useQuery<Subscription>({
    queryKey: ['subscription', orgSlug],
    queryFn: () => api.get(`billing/${orgSlug}/subscription/`).then((r) => r.data),
    enabled: !!orgSlug,
  });
}

/**
 * Live cost breakdown for the org's currently SUBSCRIBED modules (contract §2).
 * For pre-checkout previews of a not-yet-subscribed selection there is no
 * backend endpoint — compute locally from `useModulePrices` (see BillingPage).
 */
export function useSubscriptionSummary(
  orgSlug: string | undefined,
  billingCycle: 'monthly' | 'annual',
  /** T2782: `false` za vlogo, ki ji zaledje (`IsOrganizationAdmin`) pot zavrne. */
  enabled = true,
) {
  return useQuery<SubscriptionSummary>({
    queryKey: ['subscription-summary', orgSlug, billingCycle],
    queryFn: () =>
      api
        .get(`billing/${orgSlug}/subscription/summary/`, {
          params: { billing_cycle: billingCycle },
        })
        .then((r) => r.data),
    enabled: !!orgSlug && enabled,
  });
}

/**
 * Sysadmin Module Pricing list (contract §8). Used both by the org BillingPage
 * (to compute a local pre-checkout total) and by SysadminModulePricing (CRUD).
 */
export function useModulePrices(moduleId?: number) {
  return useQuery<ModulePrice[]>({
    queryKey: ['module-prices', moduleId ?? null],
    queryFn: () =>
      api
        .get('billing/module-prices/', {
          params: moduleId ? { module_id: moduleId } : undefined,
        })
        .then((r) => (Array.isArray(r.data) ? r.data : (r.data.results ?? []))),
  });
}

export interface BillingConfig {
  prices_include_vat: boolean;
  default_vat_rate: string;
  default_currency: string;
}

/** Pricing config (net/gross mode + VAT rate) for the cost-summary breakdown. */
export function useBillingConfig() {
  return useQuery<BillingConfig>({
    queryKey: ['billing-config'],
    queryFn: () => api.get('billing/config/').then((r) => r.data),
  });
}

/**
 * T1014 (S1 + S4): DDV, ki ga zaledje tej organizaciji dejansko zaračuna
 * (0 % ob obrnjeni davčni obveznosti / izvozu), in privzeta plačilna metoda po
 * državi (NL → iDEAL, BE → Bancontact) — preslikava živi samo v zaledju.
 */
export interface PricingContext {
  prices_include_vat: boolean;
  vat_rate: string;
  vat_treatment: 'standard' | 'reverse_charge' | 'exempt' | 'exempt_small_business';
  currency: string;
  default_payment_method: string | null;
}

export function usePricingContext(orgSlug: string | undefined, enabled = true) {
  return useQuery<PricingContext>({
    queryKey: ['billing-pricing-context', orgSlug],
    queryFn: () => api.get(`billing/${orgSlug}/pricing-context/`).then((r) => r.data),
    enabled: !!orgSlug && enabled,
  });
}

export interface InvoiceReadiness {
  ready: boolean;
  /** Canonical org field keys still empty (e.g. ['tax_number', 'city']). */
  missing_fields: string[];
}

/** T864: are the org's FURS invoice fields complete? Drives the soft banner. */
export function useInvoiceReadiness(orgSlug: string | undefined, enabled = true) {
  return useQuery<InvoiceReadiness>({
    queryKey: ['invoice-readiness', orgSlug],
    queryFn: () =>
      api.get(`billing/${orgSlug}/invoice-readiness/`).then((r) => r.data),
    enabled: !!orgSlug && enabled,
  });
}

export function useInvoices(orgSlug: string | undefined, enabled = true) {
  return useQuery<Invoice[]>({
    queryKey: ['invoices', orgSlug],
    queryFn: () =>
      api.get(`billing/${orgSlug}/invoices/`).then((r) =>
        Array.isArray(r.data) ? r.data : (r.data.results ?? [])
      ),
    enabled: !!orgSlug && enabled,
  });
}

/**
 * Subscribe to one or more modules and open the Mollie first-payment checkout
 * (contract §3). Resolves with the Mollie `checkout_url` to redirect to.
 */
/**
 * T2961: kaj je dokup v aktivno naročnino bremenil danes. `prorated_amount`
 * "0.00" pomeni, da razlike ni bilo (npr. dogovorjena cena) — naročnina je le
 * posodobljena.
 */
export interface CheckoutProration {
  prorated_amount: string;
  currency: string;
  billing_cycle: 'monthly' | 'annual';
  next_payment_date: string | null;
}

export interface ModuleCheckoutResponse {
  /** Prazen pri dokupu v aktivno naročnino (bremenitev prek mandata). */
  checkout_url: string;
  proration?: CheckoutProration;
}

export function useModuleCheckout(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<ModuleCheckoutResponse, Error, ModuleCheckoutRequest>({
    mutationFn: (data) =>
      api.post(`billing/${orgSlug}/checkout/`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription', orgSlug] });
      qc.invalidateQueries({ queryKey: ['organization-modules', orgSlug] });
      // T2961: dokup brez preusmeritve — odprt račun sorazmernega dela in nov
      // znesek naročnine morata biti vidna brez osvežitve strani.
      qc.invalidateQueries({ queryKey: ['invoices', orgSlug] });
      qc.invalidateQueries({ queryKey: ['subscription-summary', orgSlug] });
      qc.invalidateQueries({ queryKey: ['checkout-proration', orgSlug] });
    },
  });
}

/** T2961/T2964: predogled sorazmernega dela dokupa (`GET checkout/proration/`). */
export interface ProrationPreview {
  /** `false`: nakup ni dokup (prva naročnina se plača polno prek Mollie). */
  applies: boolean;
  billing_cycle?: 'monthly' | 'annual';
  currency?: string;
  /** Bremenjeno danes — sorazmerni del za preostanek obdobja. */
  prorated_amount?: string;
  recurring_delta?: string;
  /** Ponavljajoči znesek od naslednje obnove. */
  new_recurring_total?: string;
  next_payment_date?: string | null;
}

export function useProrationPreview(
  orgSlug: string | undefined,
  selection: Record<string, number>,
  enabled = true,
) {
  const modules = Object.keys(selection)
    .sort()
    .map((slug) => `${slug}:${selection[slug]}`)
    .join(',');
  return useQuery<ProrationPreview>({
    queryKey: ['checkout-proration', orgSlug, modules],
    queryFn: () =>
      api
        .get(`billing/${orgSlug}/checkout/proration/`, { params: { modules } })
        .then((r) => r.data),
    enabled: !!orgSlug && enabled && modules.length > 0,
    retry: false,
  });
}

/** Ena vrstica predloga sedežev — natanko tako, kot jo vrne strežnik. */
export interface CheckoutPreviewModule {
  module_slug: string;
  module_name: string;
  /** Predlagano število sedežev za ta modul (nikoli pod že plačanim). */
  seats: number;
  included_seats: number;
  base_price: string;
  seat_price: string;
  seat_overage: number;
  /**
   * T1344: koliko od `seats` jih drži **zunanji sodelavec** tega modula.
   * Zunanji dostop se podeljuje samo modulu, ki lasti projekt, zato je pri
   * ostalih modulih 0 — ravno zato, da predlog drugega modula ne naraste.
   */
  external_seats: number;
  amount: string;
}

/**
 * T1228: odgovor `GET billing/<org>/checkout/` — predlog sedežev s ceno.
 *
 * `suggested_seats` je **organizacijski** predlog (aktivna članstva + zunanji
 * sodelavci, ki držijo sedež — T1344), `seats[slug]` pa predlog **za posamezen
 * modul** — ta nikoli ne pade pod že kupljeno kapaciteto oz. vključene sedeže,
 * ker `_apply_purchased_slots` kapacitete ob aktivaciji nikoli ne zniža. Zato
 * se števili lahko razlikujeta in izbirnik sedežev seje iz `seats[slug]`,
 * sporočilo pa iz `suggested_seats`.
 */
export interface CheckoutPreview {
  billing_cycle: 'monthly' | 'annual';
  currency: string;
  active_members: number;
  /**
   * T1344: nečlani z živim zunanjim dostopom, ki držijo sedež gostiteljice.
   * Članstva po zasnovi T1227 nimajo, sedež pa porabijo — dokler jih predlog
   * ni štel, je lastniku svetoval manj sedežev, kot jih ekipa dejansko troši.
   */
  external_collaborators: number;
  suggested_seats: number;
  seats: Record<string, number>;
  modules: CheckoutPreviewModule[];
  /**
   * T2306: moduli, v katerih sedež omejuje delo (`SEAT_ENFORCED_MODULES` v
   * zaledju). Neobvezno zaradi starejšega strežnika — takrat velja prejšnje
   * vedenje (opozorilo sledi samo stanju naročnine).
   */
  seat_enforced_modules?: string[];
  /** Moduli brez cene za izbrani cikel — ne izginejo tiho iz predloga. */
  unpriced_modules: string[];
  total: string;
}

/**
 * Predlog števila sedežev + izračunana cena PRED plačilom (T1228).
 *
 * Brez tega je prehod iz preizkusa v naročnino najostrejši trenutek zasnove:
 * med preizkusom sedeži ne omejujejo nikogar, ob prvem plačilu pa je
 * `purchased_slots` privzeto 1 in ekipa petih naslednjo sekundo dela v enem.
 *
 * Brez `module_slugs` strežnik privzame module, ki jih organizacija ta hip
 * **preizkuša** — točno prehod, ki mu predlog služi. Vračilo je izračun, ne
 * zapis: klic ničesar ne shrani in ne sproži plačila.
 *
 * `retry: false` je namerno — pogled je za lastnika (`IsOrganizationOwner`),
 * zato skrbnik z `org.edit` dobi 403; ponavljanje takega zahtevka ne bi nikoli
 * uspelo, odjemalec pa mora v tem primeru samo pasti nazaj na staro vedenje.
 */
export function useCheckoutPreview(
  orgSlug: string | undefined,
  billingCycle: 'monthly' | 'annual',
  enabled = true,
) {
  return useQuery<CheckoutPreview>({
    queryKey: ['checkout-preview', orgSlug, billingCycle],
    queryFn: () =>
      api
        .get(`billing/${orgSlug}/checkout/`, {
          params: { billing_cycle: billingCycle },
        })
        .then((r) => r.data),
    enabled: !!orgSlug && enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export function useCancelSubscription(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<{ status: string }, Error, void>({
    mutationFn: () =>
      api.post(`billing/${orgSlug}/subscription/cancel/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription', orgSlug] }),
  });
}

/** T2971: izid načrtovanja ali preklica preklopa cikla. */
export interface CycleSwitchResult {
  status: 'scheduled' | 'none';
  billing_cycle: 'monthly' | 'annual';
  starts_at: string | null;
}

/** T2971: aktivna mesečna naročnina preide na letno ob izteku obdobja. */
export function useScheduleCycleSwitch(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<CycleSwitchResult, Error, void>({
    mutationFn: () =>
      api
        .post(`billing/${orgSlug}/subscription/cycle-switch/`, { billing_cycle: 'annual' })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription', orgSlug] }),
  });
}

/** T2971: preklic načrtovanega preklopa, dokler ne začne veljati. */
export function useCancelCycleSwitch(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<CycleSwitchResult, Error, void>({
    mutationFn: () =>
      api.post(`billing/${orgSlug}/subscription/cycle-switch/cancel/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription', orgSlug] }),
  });
}

function invalidateStorageAddon(qc: ReturnType<typeof useQueryClient>, orgSlug: string) {
  qc.invalidateQueries({ queryKey: ['subscription', orgSlug] });
  qc.invalidateQueries({ queryKey: ['subscription-summary', orgSlug] });
  qc.invalidateQueries({ queryKey: ['invoices', orgSlug] });
}

/** T2029: lastnik kupi paket(e) dodatnega prostora. */
export function useStorageAddonPurchase(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<StorageAddonResult, Error, { packs: number }>({
    mutationFn: (data) =>
      api.post(`billing/${orgSlug}/storage-addon/`, data).then((r) => r.data),
    onSuccess: () => invalidateStorageAddon(qc, orgSlug),
  });
}

/** T2029: odpoved paketa — učinkuje ob naslednji obnovi. */
export function useStorageAddonCancel(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<StorageAddonResult, Error, { packs: number }>({
    mutationFn: (data) =>
      api.post(`billing/${orgSlug}/storage-addon/cancel/`, data).then((r) => r.data),
    onSuccess: () => invalidateStorageAddon(qc, orgSlug),
  });
}

/** One active seat: which user holds which module. */
export interface ModuleSeat {
  module_slug: string;
  user_id: number;
}

/**
 * T1228 → **T1446**: whole seat matrix in a single payload.
 *
 * `seats` are the ACTIVE assignments, `seat_capacity` the per-module truth about
 * the limit. Both come from one request on purpose: the "3/5" counter is derived
 * by counting `seats` per slug against `seat_capacity[slug].total`, so the
 * counter and the ticked boxes can never contradict each other mid-flight (which
 * they would if the count came from `subscription.seats_by_module` and only one
 * of the two queries had refetched yet).
 *
 * `seat_capacity` is also the authoritative column list: the backend
 * (`ModuleSeatListView`, T1433) drops the modules where a seat means nothing and
 * never will (`no_access`, `free_module`), and keeps the ones where it is real
 * but *currently* unenforced (`trial`, `promo`) — the admin must still be able
 * to hand out seats for the day the free period ends.
 *
 * **Zakaj ni več `purchased_slots` (T1446).** T1433 je to polje spremenil v
 * izpeljavo iz `seat_capacity`, ki je med promocijo in preizkusom **prazna** —
 * matrika je zato ostala brez stolpcev in brez pojasnila. Ključi so že urejeni
 * po `module__sort_order` (`seat_capacity_by_module`), zato vrstni red ostane
 * last zaledja. Zaledje polje še vedno pošilja kot podedovano, a ga ta odjemalec
 * ne bere več; odsotnost številke je odslej povedana z `enforced`/`reason` in ne
 * z manjkajočim ključem (T1391).
 */
/**
 * T3169: oseba, ki ni član organizacije, a zaseda njen sedež ali mesto v
 * preizkusu (zunanji sodelavec). Matrika jo izriše brez kontrolnika, da se
 * vrstice ujemajo s števcem stolpca.
 */
export interface ExternalSeatHolder {
  user_id: number;
  email: string;
  full_name: string;
  avatar: string | null;
  modules: string[];
}

export interface ModuleSeatsResponse {
  seats: ModuleSeat[];
  seat_capacity: Record<string, SeatCapacity>;
  /** T3169 — starejše zaledje polja nima. */
  external_holders?: ExternalSeatHolder[];
}

const seatsKey = (orgSlug: string | undefined) => ['module-seats', orgSlug] as const;

/**
 * GET billing/<org>/seats/ — drives the SeatMatrix. Org admins only.
 *
 * T1336 (b): `enabled` je **drugi argument in ne notranja domneva**. Pot je na
 * zaledju `IsOrganizationAdmin` (`apps/billing/views.py:1454`), zato jo sme
 * poklicati samo lastnik ali administrator organizacije; klicatelji, ki živijo
 * tudi pri nižjih vlogah (dialog za zunanjega sodelavca — tam sme dodajati
 * tudi vodja projekta), morajo poizvedbo znati **ne** sprožiti. Zahtevek, ki
 * zanesljivo vrne 403, ni brezplačen: v konzoli je videti kot okvara.
 */
export function useModuleSeats(orgSlug: string | undefined, enabled = true) {
  return useQuery<ModuleSeatsResponse>({
    queryKey: seatsKey(orgSlug),
    queryFn: () => api.get(`billing/${orgSlug}/seats/`).then((r) => r.data),
    enabled: !!orgSlug && enabled,
  });
}

/** Zasedenost sedežev enega modula. `total` je plačana kapaciteta. */
export interface ModuleSeatUsage {
  used: number;
  total: number;
  free: number;
}

/**
 * Koliko sedežev danega modula je zasedenih / prostih (T1336 b).
 *
 * Vrne `null` v **dveh** primerih, in oba pomenita isto za klicatelja — »o tem
 * modulu ni številke, ki bi jo smel izreči«:
 *
 * 1. modula v `seat_capacity` ni: ključi so samo tisti, pri katerih sedež sploh
 *    kaj pomeni (`SEAT_ENFORCED_MODULES`, gl. `ModuleSeatListView`);
 * 2. `enforced === false` (preizkus, promocija): sedeži ta hip nikogar ne
 *    omejujejo, zato je »0 od 0« lažna trditev — pravilno je, da števca ni.
 *    **Kdor mora uporabniku povedati, ZAKAJ številke ni, bere `reason`**
 *    (T1446); ta pomočnik razloga namenoma ne razlaga, ker ni izrisovalec.
 *
 * T1446: vir je odslej `seat_capacity` in ne izpeljano `purchased_slots` —
 * drugače bi bila razlika med »ni podatka« in »ne omejuje« spet izgubljena.
 *
 * Aritmetika je tu in ne pri klicateljih z istim razlogom kot v `seatUsage.ts`:
 * dve mesti, ki isto stvar štejeta vsako po svoje, si prej ali slej
 * nasprotujeta.
 */
export function moduleSeatUsage(
  data: ModuleSeatsResponse | undefined,
  moduleSlug: string,
): ModuleSeatUsage | null {
  const capacity = data?.seat_capacity?.[moduleSlug];
  // T3010: samo plačani sedeži — meja v preizkusu (`trialSeatLimit`) ni števec matrike.
  if (data === undefined || !capacity || !isPaidSeatEnforced(capacity) || capacity.total === null) {
    return null;
  }
  const total = capacity.total;
  const used = data.seats.filter((seat) => seat.module_slug === moduleSlug).length;
  return { used, total, free: Math.max(0, total - used) };
}

/** Assign a user to a module seat (contract §5). 402 → SEAT_LIMIT_REACHED. */
export function useAssignSeat(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<
    { status: string; used_seats: number; purchased_slots: number },
    Error,
    { moduleSlug: string; userId: number }
  >({
    mutationFn: ({ moduleSlug, userId }) =>
      api
        .post(`billing/${orgSlug}/modules/${moduleSlug}/seats/`, { user_id: userId })
        .then((r) => r.data),
    // T1228: `onSettled`, NE `onSuccess` — neuspeh je prav tisti trenutek, ko
    // vemo, da je odjemalčeva kopija zastarela. 402 SEAT_LIMIT_REACHED pomeni,
    // da je nekdo drug medtem vzel zadnji sedež; če bi osvežili samo ob uspehu,
    // bi glava še naprej kazala »4/5« in polje ostalo odprto, klik pa bi vsakič
    // znova padel. Enotni odgovor `useModuleSeats` prepreči razhajanje števca
    // in kljukic le, dokler je svež.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: seatsKey(orgSlug) });
      qc.invalidateQueries({ queryKey: ['subscription', orgSlug] });
      qc.invalidateQueries({ queryKey: ['organization-modules', orgSlug] });
    },
  });
}

/** Unassign a user from a module seat (contract §6). */
export function useUnassignSeat(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<
    { status: string; used_seats: number },
    Error,
    { moduleSlug: string; userId: number }
  >({
    mutationFn: ({ moduleSlug, userId }) =>
      api
        .delete(`billing/${orgSlug}/modules/${moduleSlug}/seats/${userId}/`)
        .then((r) => r.data),
    // Isti razlog kot pri dodelitvi: padel odvzem (npr. 404 na sedežu, ki ga je
    // medtem odvzel nekdo drug) pusti kljukico obkljukano, dokler ne osvežimo.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: seatsKey(orgSlug) });
      qc.invalidateQueries({ queryKey: ['subscription', orgSlug] });
      qc.invalidateQueries({ queryKey: ['organization-modules', orgSlug] });
    },
  });
}
